// =============================================================================
// SaaS-Admin › Invoices & receipts
// Live data from GET /saastracker/admin/invoices.
//
// An invoice here is always the receipt for a completed top-up: InvoiceService
// raises one per purchase. That has consequences the mock did not reflect —
// there is no due date, nothing is ever "overdue", and there are no drafts.
// Status is DERIVED from the invoice's transaction, so it reads paid / pending /
// failed / refunded rather than the subscription-billing vocabulary.
//
// The PDF is the branded QuestPDF receipt, fetched with the bearer token rather
// than linked, because the route is auth-gated.
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../services/adminDataService';

const PAGE_SIZE = 25;

const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'paid', label: 'Paid' },
  { key: 'pending', label: 'In flight' },
  { key: 'failed', label: 'Failed' },
  { key: 'refunded', label: 'Refunded' },
];

// ---- formatting -------------------------------------------------------------

const fmtMoney = (n: number | null | undefined, ccy: string = 'USD'): string =>
  `${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

/** <input type="date"> gives a bare day; the API wants an instant. */
const startOfDay = (day: string): string | undefined => (day ? `${day}T00:00:00Z` : undefined);
const endOfDay = (day: string): string | undefined => (day ? `${day}T23:59:59Z` : undefined);

// ---- page -------------------------------------------------------------------

export const InvoicesReceipts: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const [data, setData] = useState<api.AdminInvoiceListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  const flash = useCallback((m: string) => {
    setErr(''); setMsg(m); window.setTimeout(() => setMsg(''), 3000);
  }, []);
  const fail = useCallback((e: any) => {
    setMsg(''); setErr(e?.message ?? String(e));
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!showHelp) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowHelp(false); };
    const onDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setShowHelp(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [showHelp]);

  const query = useCallback((): api.InvoiceQuery => ({
    search, status, from: startOfDay(from), to: endOfDay(to),
  }), [search, status, from, to]);

  // Guards against a slow early response overwriting a newer one.
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setErr('');
    try {
      const res = await api.getInvoices({ ...query(), page, pageSize: PAGE_SIZE });
      if (id === reqId.current) setData(res);
    } catch (e) {
      if (id === reqId.current) { setData(null); fail(e); }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [query, page, fail]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setSearchInput(''); setSearch(''); setStatus('all'); setFrom(''); setTo(''); setPage(1);
  };

  const viewPdf = async (row: api.AdminInvoiceRow) => {
    setBusyId(row.id);
    try {
      await api.openInvoicePdf(row.pdfPath, `${row.number}.pdf`);
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = await api.getAllInvoices(query());
      const head = [
        'Invoice', 'Tenant', 'Email', 'Amount', 'Currency', 'Status',
        'Issued', 'Paid', 'Emailed', 'Provider', 'Order id', 'Capture id',
      ];
      const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [
        head.join(','),
        ...rows.map(r => [
          r.number, r.tenantName, r.tenantEmail, r.amount, r.currency, r.status,
          r.issuedAt, r.paidAt, r.emailedAt, r.provider, r.orderId, r.captureId,
        ].map(esc).join(',')),
      ].join('\n');

      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      flash(`Exported ${rows.length} invoice${rows.length === 1 ? '' : 's'}`);
    } catch (e) {
      fail(e);
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const ccy = data?.currency ?? 'USD';
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstOnPage = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Invoices &amp; receipts</h1>
          <p className="ov-header__sub">
            {loading && !data
              ? 'Loading invoices…'
              : `${total.toLocaleString('en-US')} invoice${total === 1 ? '' : 's'} match the current filters.`}
          </p>
        </div>
        <div className="ov-header__actions">
          <button className="btn-small" onClick={load} disabled={loading}>
            <span className="material-icons">refresh</span> Refresh
          </button>
          <button className="btn-small" onClick={exportCsv} disabled={exporting || total === 0}>
            <span className="material-icons">file_download</span> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>

          <div className="rem-help" ref={helpRef}>
            <button
              type="button"
              className="rem-help__btn"
              aria-expanded={showHelp}
              aria-label="How this page works"
              title="How this page works"
              onClick={() => setShowHelp(v => !v)}
            >
              <span className="material-icons">help_outline</span>
            </button>
            {showHelp && (
              <div className="rem-help__panel" role="dialog" aria-label="How this page works">
                <button
                  type="button"
                  className="rem-help__close"
                  aria-label="Close"
                  onClick={() => setShowHelp(false)}
                >
                  <span className="material-icons">close</span>
                </button>
                <p>
                  Every invoice is the receipt for a credit top-up — one is raised per
                  completed purchase. Because credit is prepaid there is no due date,
                  nothing is ever overdue, and there are no drafts.
                </p>
                <p>
                  Status comes from the payment, not the invoice: <strong>paid</strong> once
                  the capture completed, <strong>in flight</strong> while it is still
                  settling, then <strong>failed</strong> or <strong>refunded</strong>. The
                  three money tiles cover the whole book, not just the rows shown below.
                </p>
              </div>
            )}
          </div>
        </div>
      </header>

      {(msg || err) && (
        <div style={{
          margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: err ? '#fdecea' : '#e7f6ec', color: err ? '#c0392b' : '#1e7e46',
          border: `1px solid ${err ? '#f5c6cb' : '#bfe6cd'}`,
        }}>
          {err || msg}
          {err && <button className="btn-small" style={{ marginLeft: 10 }} onClick={load}>Retry</button>}
        </div>
      )}

      <section className="ov-kpis">
        <div className="kpi">
          <div className="kpi__icon kpi__icon--green"><span className="material-icons">paid</span></div>
          <div className="kpi__main">
            <div className="kpi__num">{fmtMoney(data?.totalCollected, ccy)}</div>
            <div className="kpi__label">Collected</div>
            <div className="kpi__sub">all time</div>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi__icon kpi__icon--blue"><span className="material-icons">schedule</span></div>
          <div className="kpi__main">
            <div className="kpi__num">{fmtMoney(data?.totalPending, ccy)}</div>
            <div className="kpi__label">In flight</div>
            <div className="kpi__sub">capture not settled</div>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi__icon kpi__icon--warn"><span className="material-icons">undo</span></div>
          <div className="kpi__main">
            <div className="kpi__num">{fmtMoney(data?.totalRefunded, ccy)}</div>
            <div className="kpi__label">Refunded</div>
            <div className="kpi__sub">returned to tenants</div>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi__icon kpi__icon--purple"><span className="material-icons">receipt_long</span></div>
          <div className="kpi__main">
            <div className="kpi__num">{total.toLocaleString('en-US')}</div>
            <div className="kpi__label">Invoices</div>
            <div className="kpi__sub">matching the filters</div>
          </div>
        </div>
      </section>

      {/* Filters — all applied server-side */}
      <div className="filters">
        <div className="filters__search">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder="Search by invoice number or tenant…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
        <div className="filters__group">
          <label htmlFor="inv-from">Issued from</label>
          <input
            id="inv-from" type="date" value={from}
            onChange={e => { setFrom(e.target.value); setPage(1); }}
          />
        </div>
        <div className="filters__group">
          <label htmlFor="inv-to">Issued to</label>
          <input
            id="inv-to" type="date" value={to}
            onChange={e => { setTo(e.target.value); setPage(1); }}
          />
        </div>
        <button className="btn-small btn-small--ghost" onClick={resetFilters}>Reset</button>
      </div>

      <div className="invoice-tabs">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            className={`invoice-tab${status === t.key ? ' invoice-tab--active' : ''}`}
            onClick={() => { setStatus(t.key); setPage(1); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="ov-section" style={{ padding: 0 }}>
        <div className="adm-inv-table">
          <div className="adm-inv-table__head">
            <div>Invoice #</div><div>Tenant</div><div>Issued</div><div>Paid</div>
            <div>Status</div><div>Amount</div><div>Action</div>
          </div>

          {items.map(i => (
            <div className="adm-inv-row" key={i.id}>
              <div className="adm-inv-row__num" title={i.number}>{i.number}</div>
              <div className="adm-inv-row__tenant">
                <span className="adm-inv-row__tenant-main">
                  {i.tenantName}
                  <em>{i.tenantEmail || 'no email on file'}</em>
                </span>
              </div>
              <div className="adm-inv-row__date">{fmtDate(i.issuedAt)}</div>
              <div className="adm-inv-row__date" title={i.paidAt ? fmtDateTime(i.paidAt) : undefined}>
                {fmtDate(i.paidAt)}
              </div>
              <div><span className={`inv-status inv-status--${i.status}`}>{i.status}</span></div>
              <div className="adm-inv-row__amount">{fmtMoney(i.amount, i.currency)}</div>
              <div>
                <button
                  className="btn-small"
                  onClick={() => viewPdf(i)}
                  disabled={busyId === i.id}
                  title={`Open the receipt for ${i.number}`}
                >
                  <span className="material-icons">picture_as_pdf</span>
                  {busyId === i.id ? '…' : 'PDF'}
                </button>
              </div>
            </div>
          ))}

          {loading && items.length === 0 && (
            <div className="empty">
              <span className="material-icons">hourglass_empty</span>
              Loading invoices…
            </div>
          )}

          {!loading && items.length === 0 && !err && (
            <div className="empty">
              <span className="material-icons">inbox</span>
              No invoices match these filters.
            </div>
          )}

          {!loading && err && (
            <div className="empty">
              <span className="material-icons">cloud_off</span>
              Could not reach the SaaS Tracker API.
              <button className="btn-small" onClick={load}>Retry</button>
            </div>
          )}
        </div>
      </div>

      {total > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 12, fontSize: 12, color: '#6b7684',
        }}>
          <span>Showing {firstOnPage}–{lastOnPage} of {total.toLocaleString('en-US')}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-small" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              <span className="material-icons">chevron_left</span> Prev
            </button>
            <span>Page {page} of {pageCount}</span>
            <button className="btn-small" disabled={page >= pageCount || loading} onClick={() => setPage(p => p + 1)}>
              Next <span className="material-icons">chevron_right</span>
            </button>
          </span>
        </div>
      )}
    </div>
  );
};
